import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { db } from "@/lib/firebase";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { getNextDocumentNumber } from "@/lib/documentNumber";
import { printDocument } from "@/lib/printHelper";
import QuotationForm from "@/components/forms/QuotationForm";
import QuotationPreview from "@/components/preview/QuotationPreview";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Printer, Save, RotateCcw, Eye, Edit, Loader2 } from "lucide-react";

const getInitialFormData = (quotationNumber = "") => ({
  quotationNumber,
  quotationDate: new Date().toISOString().split("T")[0],
  validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  customerName: "",
  customerAddress: "",
  customerPhone: "",
  hypothecation: "",
  vehicleModel: "",
  vehicleVariant: "",
  vehicleColor: "",
  exShowroomPrice: "",
  rtoCharges: "",
  insuranceCharges: "",
  hpaCharges: "",
  handlingCharges: "",
  accessoriesCharges: "",
  otherCharges: "",
  discount: "",
  notes: "",
});

const QuotationPage = () => {
  const [formData, setFormData] = useState(getInitialFormData("Loading..."));
  const [activeTab, setActiveTab] = useState("form");
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [numberLoading, setNumberLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    const loadNumber = async () => {
      setNumberLoading(true);
      const num = await getNextDocumentNumber("QUO", "quotations", "quotationNumber");
      setFormData(getInitialFormData(num));
      setNumberLoading(false);
    };
    loadNumber();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = async () => {
    setNumberLoading(true);
    const num = await getNextDocumentNumber("QUO", "quotations", "quotationNumber");
    setFormData(getInitialFormData(num));
    setNumberLoading(false);
    setActiveTab("form");
    toast.success("Form reset successfully");
  };

  const calculateTotal = () => {
    const subtotal =
      (parseFloat(formData.exShowroomPrice) || 0) +
      (parseFloat(formData.rtoCharges) || 0) +
      (parseFloat(formData.insuranceCharges) || 0) +
      (parseFloat(formData.hpaCharges) || 0) +
      (parseFloat(formData.handlingCharges) || 0) +
      (parseFloat(formData.accessoriesCharges) || 0) +
      (parseFloat(formData.otherCharges) || 0);
    return Math.max(0, subtotal - (parseFloat(formData.discount) || 0));
  };

  // Required fields validation
  const validate = () => {
    const required = [
      { key: "customerName", label: "Customer Name" },
      { key: "customerPhone", label: "Mobile Number" },
      { key: "customerAddress", label: "Customer Address" },
      { key: "vehicleModel", label: "Vehicle Model" },
      { key: "exShowroomPrice", label: "Ex-Showroom Price" },
    ];
    for (const f of required) {
      if (!formData[f.key] || formData[f.key].toString().trim() === "") {
        toast.error(`Please fill in: ${f.label}`);
        setActiveTab("form");
        return false;
      }
    }
    if (!parseFloat(formData.exShowroomPrice) || parseFloat(formData.exShowroomPrice) <= 0) {
      toast.error("Ex-Showroom Price must be greater than 0");
      setActiveTab("form");
      return false;
    }
    return true;
  };

  const saveToDb = async () => {
    await addDoc(collection(db, "quotations"), {
      ...formData,
      total: calculateTotal(),
      savedAt: Timestamp.now(),
    });
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await saveToDb();
      toast.success(`Quotation ${formData.quotationNumber} saved successfully!`);
      const nextNum = await getNextDocumentNumber("QUO", "quotations", "quotationNumber");
      setFormData(getInitialFormData(nextNum));
      setActiveTab("form");
    } catch (error) {
      toast.error("Failed to save quotation. Check Firebase configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!validate()) return;

    // 1. Switch to preview tab FIRST so the document renders completely
    setActiveTab("preview");
    setIsPrinting(true);

    // 2. Wait for React to paint the preview tab (two animation frames + buffer)
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 3. Open the browser print dialog via printHelper (clones to body level for reliable A4)
    await printDocument();

    // 4. After print dialog closes: save to DB, show toast, reset form
    try {
      await saveToDb();
      toast.success(`Quotation ${formData.quotationNumber} saved & printed!`);
      const nextNum = await getNextDocumentNumber("QUO", "quotations", "quotationNumber");
      setFormData(getInitialFormData(nextNum));
      setActiveTab("form");
    } catch (error) {
      toast.error("Printed but failed to save. Check Firebase.");
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">Vehicle Quotation</h2>
          <p className="text-sm text-muted-foreground">Create and print quotation for E-Rickshaw / Toto</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw className="h-4 w-4" /> Reset
          </Button>
          <Button variant="secondary" onClick={handleSave} className="gap-2" disabled={isSaving || numberLoading}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handlePrint} className="gap-2 bg-primary text-primary-foreground" disabled={isPrinting || numberLoading}>
            {isPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            {isPrinting ? "Saving & Printing..." : "Print"}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 no-print">
          <TabsTrigger value="form" className="gap-2"><Edit className="h-4 w-4" /> Edit Form</TabsTrigger>
          <TabsTrigger value="preview" className="gap-2"><Eye className="h-4 w-4" /> Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="form" className="mt-4">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading">Quotation Details</CardTitle></CardHeader>
            <CardContent>
              <QuotationForm
                formData={formData}
                onInputChange={handleInputChange}
                onSelectChange={handleSelectChange}
                calculateTotal={calculateTotal}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <div className="no-print mb-3 p-3 bg-secondary/30 rounded-lg border border-border text-sm text-muted-foreground">
            Click <strong>Print</strong> to send to printer. The preview below shows the exact A4 output.
          </div>
          <QuotationPreview ref={printRef} formData={formData} total={calculateTotal()} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuotationPage;
